import * as jsonPatch from 'fast-json-patch';
import DB, { Changeset, Entry } from '../db/DB';
import type ReplicantMigrator from '../ReplicantMigrator';
import { deepCopy } from '../utils/ObjUtils';
import { duration } from '../utils/Utils';

export type Diff = {
    state?: jsonPatch.Operation[];
    metainfo?: jsonPatch.Operation[];
};

export type SnapshotUpdate = {
    dailySnapshotAt?: number;
    dailySnapshotRev?: number;
    weeklySnapshotAt?: number;
    weeklySnapshotRev?: number;
    monthlySnapshotAt?: number;
    monthlySnapshotRev?: number;
    shouldWriteNewSnapshot: boolean;
    expirationsMs: { [rev: number]: number };
};

/**
 * Set up observers to listen for changes in `entry.state` and `entry.metadata`.
 *
 * @returns Function that removes observers from `entry` and returns any observed changes.
 */
export function observe(entry: Entry<object>): () => Diff {
    const stateObserver = jsonPatch.observe<object>(entry.state);
    const metainfoObserver = jsonPatch.observe<object>(entry.metainfo);

    return () => {
        const statePatches = jsonPatch.generate(stateObserver);
        const metainfoPatches = jsonPatch.generate(metainfoObserver);

        stateObserver.unobserve();
        metainfoObserver.unobserve();

        return {
            state: statePatches.length > 0 ? statePatches : undefined,
            metainfo: metainfoPatches.length > 0 ? metainfoPatches : undefined,
        };
    };
}

export function applyChangesetsToSnapshot(opts: {
    changesets: Changeset[];
    migrator: ReplicantMigrator;
    snapshot: Entry<any>;
}): Entry<any> {
    const entry = deepCopy(opts.snapshot);

    for (const changeset of opts.changesets) {
        if (changeset.rev !== entry.rev + 1) {
            throw new Error(`Cannot apply changeset revision ${changeset.rev} to entry revision ${entry.rev}`);
        }
        if (changeset.version !== entry.version && changeset.version !== entry.version + 1) {
            throw new Error(`Cannot apply changeset version ${changeset.version} to entry version ${entry.version}`);
        }

        if (changeset.version === entry.version + 1) {
            opts.migrator.applyMigration(entry.state, entry.version);
            entry.version = changeset.version;
        }

        if (changeset.diff?.metainfo) {
            jsonPatch.applyPatch(entry.metainfo, changeset.diff.metainfo, true);
        }
        if (changeset.diff?.state) {
            jsonPatch.applyPatch(entry.state, changeset.diff.state, true);
        }

        entry.rev = changeset.rev;
        entry.mainItemRev = changeset.rev;
        entry.lastUpdated = changeset.date.getTime();
    }

    return entry;
}

/**
 * Return snapshot at `opts.snapshotRev` patched with JSON-Patch diffs from changesets up to
 * `opts.targetRev` (inclusive).
 */
export async function getRecoveredEntry(opts: {
    db: DB<any>;
    id: string;
    migrator: ReplicantMigrator;
    snapshotRev: number;
    targetRev: number;
}): Promise<Entry<{ [key: string]: unknown }>> {
    if (opts.snapshotRev >= opts.targetRev) {
        throw new Error('Recovery target revision must be higher than snapshot revision');
    }

    const snapshot = await opts.db.snapshots.fetchSnapshot(opts.id, opts.snapshotRev);
    if (!snapshot) {
        throw new Error(`No snapshot found for user ${opts.id} at rev ${opts.snapshotRev}`);
    }

    const changesets = await opts.db.fetchChangesetsBetween(opts.id, snapshot.rev + 1, opts.targetRev);

    if (changesets.length !== opts.targetRev - opts.snapshotRev) {
        let missingRev: number;

        for (let rev = snapshot.rev + 1; rev <= opts.targetRev; rev++) {
            if (!changesets.find((changeset) => changeset.rev === rev)) {
                missingRev = rev;
                break;
            }
        }

        throw new Error(`No changeset found for user ${opts.id} at rev ${missingRev!}`);
    }

    return applyChangesetsToSnapshot({ changesets, migrator: opts.migrator, snapshot });
}

/**
 * Update snapshot timestamps and revisions if enough time has passed since previous snapshot.
 *
 * @returns description of snapshot update operations to be performed alongside changeset write.
 */
export function updateSnapshotTimestamps(opts: { entry: Entry<any>; now: number }): SnapshotUpdate {
    const { entry, now } = opts;
    const { dailySnapshotAt, weeklySnapshotAt, monthlySnapshotAt } = entry;
    const result: SnapshotUpdate = { shouldWriteNewSnapshot: false, expirationsMs: {} };

    if (!dailySnapshotAt || now >= dailySnapshotAt + duration({ days: 1 })) {
        result.shouldWriteNewSnapshot = true;

        const { dailySnapshotRev, weeklySnapshotRev, monthlySnapshotRev } = entry;

        if (dailySnapshotRev) {
            // Previous daily snapshot's expiration date depends on whether it's also a weekly/monthly snapshot:
            const prevDailySnapshotExpiresAt =
                dailySnapshotRev === monthlySnapshotRev
                    ? monthlySnapshotAt! + duration({ days: 90 })
                    : dailySnapshotRev === weeklySnapshotRev
                    ? weeklySnapshotAt! + duration({ days: 30 })
                    : dailySnapshotAt! + duration({ days: 7 });

            // Ensure that expiration date is always a future date:
            if (prevDailySnapshotExpiresAt < now + duration({ hours: 1 })) {
                result.expirationsMs[dailySnapshotRev] = now + duration({ days: 7 });
            } else {
                result.expirationsMs[dailySnapshotRev] = prevDailySnapshotExpiresAt;
            }
        }

        entry.dailySnapshotAt = now;
        entry.dailySnapshotRev = entry.rev;
        result.dailySnapshotAt = now;
        result.dailySnapshotRev = entry.rev;
    }

    if (!monthlySnapshotAt || now >= monthlySnapshotAt + duration({ days: 30 })) {
        result.shouldWriteNewSnapshot = true;
        entry.monthlySnapshotAt = now;
        entry.monthlySnapshotRev = entry.rev;
        result.monthlySnapshotAt = now;
        result.monthlySnapshotRev = entry.rev;
    }

    if (!weeklySnapshotAt || now >= weeklySnapshotAt + duration({ days: 7 })) {
        result.shouldWriteNewSnapshot = true;
        entry.weeklySnapshotAt = now;
        entry.weeklySnapshotRev = entry.rev;
        result.weeklySnapshotAt = now;
        result.weeklySnapshotRev = entry.rev;
    }

    return result;
}
